import { emptyApi } from "store/apis/emptyApi";

type ConfirmCompanyResponse = {
  group_id: string;
  entity_id: string;
  company_name: string;
};

type ApiState = {
  uuid: string;
  name: string;
};

type ApiCountry = {
  country_id: string;
  code_alpha_2: string;
  name: string;
};

type ApiPerson = {
  uuid: string;
  first_name: string;
  last_name: string;
  email: string;
  job_title: string;
  citizenship: string;
  country_id?: string | null;
  ownership_stake: string;
};

type ApiRelatedEntity = {
  entity_id: string;
  entity_name: string;
  formation_type_id: string | null;
  formation_type: string | null;
  state_id: string | null;
  state_name: string | null;
  relation_type: string;
};

type ApiEntityReview = {
  entity_id: string;
  group_id: string;
  company_name: string;
  entity_name?: string;
  entity_suffix?: string | null;
  formation_type: string | null;
  formation_type_id?: string | null;
  country?: ApiCountry | null;
  taxed_as?: string | null;
  address_data?: AddressData | null;
  c_state?: { uuid: string; name: string } | null;
  registered_name?: string | null;
  ein?: string | null;
  incorporation_date?: string | null;
  mailing_address?: object | null;
  states?: ApiState[];
  people?: ApiPerson[];
  related_entities?: ApiRelatedEntity[];
  middesk_verification_pending?: boolean;
};

type ApiGroupReview = {
  entities: ApiEntityReview[];
};

type ApiPrefill = {
  entity_name?: string;
  entity_suffix?: string | null;
  brand_name?: string | null;
  formation_type_id?: string;
  address_data?: AddressData | null;
  incorporation_date?: string | null;
  naics_code?: string | null;
  ein?: string | null;
  middesk_verification_pending?: boolean;
};

export type AddressData = {
  street_address: string;
  street_address_line_2?: string;
  city: string;
  state?: string;
  zipcode: string;
  country: string;
};

export type OnboardingEntityInput = {
  formationTypeId: string;
  entityName: string;
  entitySuffix?: string;
  addressData?: AddressData | null;
  taxedAs?: string;
  cStateId?: string;
  dateOfIncorporation?: string;
  source?: string;
};

export type EntityRelation = {
  parentId: string;

  childId?: string;
};

export type CreateEntityArg = {
  companyName: string;
  tosPpAccepted?: boolean;
  entity: OnboardingEntityInput;
};

export type CreatedEntity = {
  groupId: string;
  entityId: string;
  companyName: string;
};

export type EntityState = {
  id: string;
  name: string;
  isHome: boolean;
};

export type RelatedEntity = {
  id: string;
  name: string;
  entityType: string;
  formationTypeId?: string;
  country?: string;
  relation: string;
};

export type EntityPersonRole =
  | "Director"
  | "Founder"
  | "Officer"
  | "Shareholder"
  | "Others";

export type EntityPerson = {
  id: string;
  name: string;
  email?: string;
  country: string;
  countryId?: string;
  roles: EntityPersonRole[];
  ownership?: string;
};

export type EntityReviewDetails = {
  registeredName: string;
  ein: string;
  dateOfIncorporation: string;
  homeStateId: string | null;
  states: EntityState[];
  relatedEntities: RelatedEntity[];
  people: EntityPerson[];
  verificationPending: boolean;
};

export type EntityReviewPatch = Partial<
  Pick<
    EntityReviewDetails,
    "registeredName" | "ein" | "dateOfIncorporation" | "states"
  >
>;

export type EntityPersonInput = {
  name: string;
  email?: string;
  countryName: string;
  countryId?: string;
  roles: EntityPersonRole[];
  ownership?: string;
};

export type EntityReviewItem = EntityReviewDetails & {
  entityId: string;
  groupId: string;
  companyName: string;
  entityName: string;
  entitySuffix: string;
  entityTypeLabel: string;
  formationTypeId: string;
  countryCode: string;
  countryId: string;
  taxedAs?: string;
  addressData: AddressData | null;
};

export type TriggerVerificationResult = {
  group_id: string;
  triggered: boolean;
};

export type OnboardingPrefill = {
  entityName: string;
  entitySuffix: string;
  brandName: string;
  formationTypeId: string;
  addressData: AddressData | null;
  incorporationDate: string;
  naicsCode: string;
  ein: string;
  verificationPending: boolean;
};

const toEntityPerson = (person: ApiPerson): EntityPerson => ({
  id: person.uuid,
  name: [person.first_name, person.last_name].filter(Boolean).join(" "),
  email: person.email ?? "",
  country: person.citizenship ?? "",
  countryId: person.country_id ?? undefined,
  roles: person.job_title
    ? (person.job_title.split(",").map((r) => r.trim()) as EntityPersonRole[])
    : [],
  ownership: person.ownership_stake || undefined,
});

const toEntityReview = (response: ApiEntityReview): EntityReviewItem => {
  const homeStateId = response.c_state?.uuid ?? null;
  const rawStates = (response.states ?? []).map((s) => ({
    id: s.uuid,
    name: s.name,
    isHome: s.uuid === homeStateId,
  }));
  const hasHome = rawStates.some((s) => s.isHome);
  if (!hasHome && rawStates.length > 0) {
    rawStates[0] = { ...rawStates[0], isHome: true };
  }
  return {
    entityId: response.entity_id,
    groupId: response.group_id,
    companyName: response.company_name ?? "",
    entityName: response.entity_name ?? "",
    entitySuffix: response.entity_suffix ?? "",
    entityTypeLabel: response.formation_type ?? "",
    formationTypeId: response.formation_type_id ?? "",
    countryCode: response.country?.code_alpha_2 ?? "",
    countryId: response.country?.country_id ?? "",
    taxedAs: response.taxed_as ?? undefined,
    addressData: response.address_data ?? null,
    registeredName: response.registered_name ?? "",
    ein: response.ein ?? "",
    dateOfIncorporation: response.incorporation_date ?? "",
    verificationPending: response.middesk_verification_pending ?? false,
    homeStateId,
    states: rawStates,
    people: (response.people ?? []).map(toEntityPerson),
    relatedEntities: (response.related_entities ?? []).map((r) => ({
      id: r.entity_id,
      name: r.entity_name,
      entityType: r.formation_type ?? "",
      relation: r.relation_type,
    })),
  };
};

const toReviewPatchPayload = (patch: EntityReviewPatch) => {
  const body: Record<string, unknown> = {};

  if ("registeredName" in patch)
    body.registered_name = patch.registeredName || null;
  if ("ein" in patch) body.ein = patch.ein || null;
  if ("dateOfIncorporation" in patch) {
    body.incorporation_date = patch.dateOfIncorporation || null;
  }
  if (patch.states) {
    body.c_state_id = patch.states.find((s) => s.isHome)?.id ?? null;
    body.state_ids = patch.states.filter((s) => !s.isHome).map((s) => s.id);
  }

  return body;
};

const toPersonPayload = (person: EntityPersonInput) => {
  const [firstName = "", ...rest] = person.name.trim().split(" ");

  return {
    first_name: firstName,
    last_name: rest.join(" "),
    email: person.email || null,
    citizenship: person.countryName,
    country_id: person.countryId ?? null,
    job_title: person.roles.join(", "),
    ownership_stake: person.ownership ?? null,
  };
};

const toReviewPayload = (
  details: EntityReviewDetails,
  registeredName: string
) => ({
  registered_name: registeredName || null,
  ein: details.ein || null,
  incorporation_date: details.dateOfIncorporation || null,
  c_state_id: details.states.find((s) => s.isHome)?.id ?? null,
  state_ids: details.states.filter((s) => !s.isHome).map((s) => s.id),
  people: details.people.map((p) => {
    const parts = p.name.trim().split(" ");
    return {
      first_name: parts[0] ?? "",
      last_name: parts.slice(1).join(" "),
      email: p.email ?? null,
      citizenship: p.country,
      country_id: p.countryId ?? null,
      job_title: p.roles.join(", "),
      ownership_stake: p.ownership ?? null,
    };
  }),
});

export const onboardingEntityApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    createOnboardingEntity: build.mutation<CreatedEntity, CreateEntityArg>({
      query: ({ companyName, tosPpAccepted, entity }) => ({
        url: "api/inkle/platform/v2/onboarding/confirm-company/",
        method: "POST",
        body: {
          company_name: companyName,
          tos_pp_accepted: tosPpAccepted ?? true,
          entity_name: entity.entityName,
          entity_suffix: entity.entitySuffix || null,
          formation_type_id: entity.formationTypeId,
          address_data: entity.addressData ?? null,
          taxed_as: entity.taxedAs || null,
        },
      }),
      transformResponse: (response: ConfirmCompanyResponse): CreatedEntity => ({
        groupId: response.group_id,
        entityId: response.entity_id,
        companyName: response.company_name,
      }),
    }),

    getOnboardingPrefill: build.query<
      OnboardingPrefill,
      { domain: string; formationTypeId?: string }
    >({
      query: ({ domain, formationTypeId }) => ({
        url:
          `api/inkle/platform/v2/onboarding/prefill/?domain=${domain}` +
          (formationTypeId ? `&formation_type_id=${formationTypeId}` : ""),
      }),
      transformResponse: (response: ApiPrefill): OnboardingPrefill => ({
        entityName: response.entity_name ?? "",
        entitySuffix: response.entity_suffix ?? "",
        brandName: response.brand_name ?? "",
        formationTypeId: response.formation_type_id ?? "",
        addressData: response.address_data ?? null,
        incorporationDate: response.incorporation_date ?? "",
        naicsCode: response.naics_code ?? "",
        ein: response.ein ?? "",
        verificationPending: Boolean(response.middesk_verification_pending),
      }),
    }),

    verifyGroupEntities: build.mutation<
      EntityReviewItem[],
      { groupId: string }
    >({
      query: ({ groupId }) => ({
        url: `api/inkle/platform/v2/onboarding/group/${groupId}/verify/`,
        method: "POST",
      }),
      transformResponse: (response: ApiGroupReview): EntityReviewItem[] =>
        (response.entities ?? []).map(toEntityReview),
      invalidatesTags: ["ONBOARDING_GROUP_REVIEW"],
    }),

    getGroupReviewDetails: build.query<EntityReviewItem[], { groupId: string }>(
      {
        query: ({ groupId }) => ({
          url: `api/inkle/platform/v2/onboarding/group/${groupId}/entity/`,
        }),
        transformResponse: (response: ApiGroupReview): EntityReviewItem[] =>
          (response.entities ?? []).map(toEntityReview),
        providesTags: ["ONBOARDING_GROUP_REVIEW", "ENTITY_DATA_ATTRIBUTES"],
      }
    ),

    saveEntityReviewDetails: build.mutation<
      EntityReviewItem,
      { entityId: string; details: EntityReviewDetails; registeredName: string }
    >({
      query: ({ entityId, details, registeredName }) => ({
        url: `api/inkle/platform/v2/onboarding/entity/${entityId}/review/`,
        method: "PUT",
        body: toReviewPayload(details, registeredName),
      }),
      transformResponse: (response: ApiEntityReview) =>
        toEntityReview(response),
    }),

    patchEntityReviewDetails: build.mutation<
      void,
      { entityId: string; groupId: string; patch: EntityReviewPatch }
    >({
      query: ({ entityId, patch }) => ({
        url: `api/inkle/platform/v2/onboarding/entity/${entityId}/review/`,
        method: "PATCH",
        body: toReviewPatchPayload(patch),
      }),
    }),

    addEntityPerson: build.mutation<
      ApiEntityReview,
      { entityId: string; groupId: string; person: EntityPersonInput }
    >({
      query: ({ entityId, person }) => ({
        url: `api/inkle/platform/v2/onboarding/entity/${entityId}/person/`,
        method: "POST",
        body: toPersonPayload(person),
      }),
      invalidatesTags: ["ONBOARDING_GROUP_REVIEW"],
    }),

    updateEntityPerson: build.mutation<
      ApiEntityReview,
      {
        entityId: string;
        groupId: string;
        personId: string;
        person: EntityPersonInput;
      }
    >({
      query: ({ entityId, personId, person }) => ({
        url: `api/inkle/platform/v2/onboarding/entity/${entityId}/person/${personId}/`,
        method: "PATCH",
        body: toPersonPayload(person),
      }),
      invalidatesTags: ["ONBOARDING_GROUP_REVIEW"],
    }),

    deleteEntityPerson: build.mutation<
      void,
      { entityId: string; groupId: string; personId: string }
    >({
      query: ({ entityId, personId }) => ({
        url: `api/inkle/platform/v2/onboarding/entity/${entityId}/person/${personId}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["ONBOARDING_GROUP_REVIEW"],
    }),

    updateOnboardingEntity: build.mutation<
      { entityId: string },
      {
        entityId: string;
        input: OnboardingEntityInput;
        relation?: EntityRelation;
      }
    >({
      query: ({ entityId, input, relation }) => ({
        url: `api/inkle/platform/v2/onboarding/entity/${entityId}/`,
        method: "PUT",
        body: {
          entity_name: input.entityName,
          entity_suffix: input.entitySuffix || null,
          formation_type_id: input.formationTypeId,
          address_data: input.addressData ?? null,
          taxed_as: input.taxedAs || null,
          c_state_id: input.cStateId || null,
          date_of_incorporation: input.dateOfIncorporation || null,
          parent_id: relation?.parentId ?? null,
          child_id: relation?.childId ?? null,
        },
      }),
      invalidatesTags: ["ONBOARDING_GROUP_REVIEW", "ENTITY_DATA_ATTRIBUTES"],
    }),

    deleteOnboardingEntity: build.mutation<
      { entityId: string; deleted: boolean },
      { entityId: string }
    >({
      query: ({ entityId }) => ({
        url: `api/inkle/platform/v2/onboarding/entity/${entityId}/`,
        method: "DELETE",
      }),
      transformResponse: (response: {
        entity_id: string;
        deleted: boolean;
      }) => ({ entityId: response.entity_id, deleted: response.deleted }),
    }),

    addGroupEntity: build.mutation<
      { entityId: string },
      {
        groupId: string;
        input: OnboardingEntityInput;
        relation?: EntityRelation;
      }
    >({
      query: ({ groupId, input, relation }) => ({
        url: `api/inkle/platform/v2/onboarding/group/${groupId}/entity/`,
        method: "POST",
        body: {
          entity_name: input.entityName,
          entity_suffix: input.entitySuffix || null,
          formation_type_id: input.formationTypeId,
          address_data: input.addressData ?? null,
          taxed_as: input.taxedAs || null,
          c_state_id: input.cStateId || null,
          date_of_incorporation: input.dateOfIncorporation || null,
          parent_id: relation?.parentId || null,
          child_id: relation?.childId || null,
          ...(input.source ? { source: input.source } : {}),
        },
      }),
      transformResponse: (response: { entity_id: string }) => ({
        entityId: response.entity_id,
      }),
      invalidatesTags: ["FILINGS_DASHBOARD", "TAX_DETECTED_FILINGS", "Groups"],
    }),

    inviteTeamMembers: build.mutation<
      unknown,
      { groupId: string; invites: Array<{ email: string }> }
    >({
      query: ({ groupId, invites }) => ({
        url: `api/inkle/platform/v2/onboarding/group/${groupId}/invite/`,
        method: "POST",
        body: { invites },
      }),
    }),

    applyOnboardingCoupon: build.mutation<
      unknown,
      { groupId: string; couponCode: string }
    >({
      query: ({ groupId, couponCode }) => ({
        url: `api/inkle/platform/v2/onboarding/group/${groupId}/coupon/`,
        method: "POST",
        body: { coupon_code: couponCode },
      }),
    }),

    triggerGroupVerification: build.mutation<
      TriggerVerificationResult,
      { groupId: string }
    >({
      query: ({ groupId }) => ({
        url: `api/inkle/platform/v2/onboarding/group/${groupId}/trigger-verification/`,
        method: "POST",
      }),
    }),
  }),
});

export const {
  useGetOnboardingPrefillQuery,
  useCreateOnboardingEntityMutation,
  useGetGroupReviewDetailsQuery,
  useVerifyGroupEntitiesMutation,
  useSaveEntityReviewDetailsMutation,
  usePatchEntityReviewDetailsMutation,
  useAddEntityPersonMutation,
  useUpdateEntityPersonMutation,
  useDeleteEntityPersonMutation,
  useUpdateOnboardingEntityMutation,
  useAddGroupEntityMutation,
  useDeleteOnboardingEntityMutation,
  useInviteTeamMembersMutation,
  useApplyOnboardingCouponMutation,
  useTriggerGroupVerificationMutation,
} = onboardingEntityApi;
